import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { FEED_KEY_VERSION, getOrCreateFeedKey, wrapFeedKeyForUsername } from '../lib/feed-key';
import { t } from '../lib/i18n';
import { api } from '../lib/trpc/client';

type ActiveCircleMember = { userId: string; scratchUsername: string; role: 'owner' | 'member'; joinedAt: string };

function roleLabel(role: 'owner' | 'member'): string {
  return role === 'owner' ? t('オーナー', 'Owner') : t('メンバー', 'Member');
}

export function InvitesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCircleId, setActiveCircleId] = useState<string | null>(null);
  const [activeCircleName, setActiveCircleName] = useState('');
  const [activeCircleMembers, setActiveCircleMembers] = useState<ActiveCircleMember[]>([]);
  const [inviteeUsername, setInviteeUsername] = useState('');
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [circles, active] = await Promise.all([api.circle.getMyCircles(), api.circle.getActiveCircle()]);
      setActiveCircleId(circles.activeCircleId);
      setActiveCircleName(active?.name ?? '');
      setActiveCircleMembers(active?.members ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('招待情報の取得に失敗しました', 'Failed to load invite information'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleInviteMember = async () => {
    const username = inviteeUsername.trim();
    if (!username || !activeCircleId) {
      return;
    }

    setInviting(true);
    setError(null);
    try {
      const feedKey = await getOrCreateFeedKey(FEED_KEY_VERSION);
      const identity = await api.user.getIdentityPk({ username });
      const wrapped = await wrapFeedKeyForUsername(feedKey, identity.identityPk);

      await api.circle.inviteMember({
        circleId: activeCircleId,
        inviteeUsername: username,
        wrappedFeedKeys: [{ version: FEED_KEY_VERSION, ciphertext: wrapped.ciphertext, ephemeralPk: wrapped.ephemeralPk }]
      });

      setInviteeUsername('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('メンバー招待に失敗しました', 'Failed to invite member'));
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <main className="mx-auto min-h-screen w-full max-w-3xl space-y-4 px-4 py-10">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-screen w-full max-w-3xl space-y-4 px-4 py-10">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold">{t('招待', 'Invites')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('現在のアクティブサークルからメンバー招待を行います。受信した招待はサークル画面で承認します。', 'Invite members from your active circle. Approve incoming invites on the circles page.')}
        </p>
      </header>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('メンバーを招待', 'Invite Members')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {activeCircleName
              ? `${t('現在のサークル', 'Current circle')}: ${activeCircleName}`
              : t('アクティブサークルが未選択です。先にサークルを切り替えてください。', 'No active circle selected. Switch circles first.')}
          </p>
          <div className="flex gap-2">
            <Input value={inviteeUsername} onChange={(e) => setInviteeUsername(e.target.value)} placeholder={t('招待するユーザー名', 'Invite username')} />
            <Button onClick={handleInviteMember} disabled={inviting || !inviteeUsername.trim() || !activeCircleId}>
              {inviting ? t('招待中...', 'Inviting...') : t('招待', 'Invite')}
            </Button>
          </div>

          {activeCircleMembers.length > 0 ? (
            <div className="rounded-md border border-border p-3 text-sm text-muted-foreground">
              {activeCircleMembers.map((member) => (
                <p key={member.userId}>
                  {member.scratchUsername} ({roleLabel(member.role)})
                </p>
              ))}
            </div>
          ) : null}

          <Button variant="outline" onClick={() => navigate('/circles')}>
            {t('サークル切り替えへ', 'Switch circles')}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
